import React, { useState, useEffect } from "react";
import { DoorOpen, Clock, Shield, Star, Wrench, CheckCircle, Phone, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

const SerramentiSection = () => {
  const [currentImage, setCurrentImage] = useState(0);

  const serramentiImages = [
    {
      url: "/images/ristrutturazioni2.jpeg",
      title: "Vetrata Panoramica Scorrevole",
      description: "Porta-finestra in legno con apertura scorrevole e vista su terrazza privata"
    },
    {
      url: "/images/ristrutturazioni1.jpeg",
      title: "Finestra in Legno Tradizionale",
      description: "Infisso in legno massello con apertura a battente e dettagli architettonici"
    },
    {
      url: "/images/about9.jpeg",
      title: "Finestra Moderna con Tapparelle",
      description: "Serramento bianco con sistema tapparelle integrate e isolamento termico"
    },
    {
      url: "/images/about7.jpeg",
      title: "Infisso Premium in Legno",
      description: "Finestra di alta qualità con profili personalizzati e finiture eleganti"
    },
    {
      url: "/images/vetrate6.jpg",
      title: "Serramento Completo",
      description: "Installazione professionale con cura dei dettagli e finitura perfetta"
    }
  ];

  // Auto-slide ogni 5 secondi
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % serramentiImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [serramentiImages.length]);

  const goToPrevious = () => {
    setCurrentImage((prev) => (prev - 1 + serramentiImages.length) % serramentiImages.length);
  };

  const goToNext = () => {
    setCurrentImage((prev) => (prev + 1) % serramentiImages.length);
  };

  const features = [
    "Infissi in PVC, alluminio e legno massello",
    "Vetri temperati e camera con gas isolante",
    "Porte blindate certificate classe 3 e 4",
    "Sistemi di apertura: battente, scorrevole, vasistas",
    "Isolamento termico fino a Uw 0.9 W/m²K",
    "Isolamento acustico fino a 42 dB",
    "Tapparelle e persiane motorizzate",
    "Zanzariere integrate e removibili",
    "Maniglie e ferramenta di sicurezza",
    "Garanzia 10 anni sui profili"
  ];

  const benefits = [
    "Riduzione consumi energetici fino al 40%",
    "Eliminazione ponti termici e spifferi",
    "Maggiore sicurezza antieffrazione",
    "Riduzione rumori esterni fino a 42 dB",
    "Aumento valore immobile del 15-20%",
    "Manutenzione ridotta e durabilità",
    "Comfort abitativo superiore",
    "Detrazioni fiscali fino al 65%"
  ];

  const windowTypes = [
    { name: "PVC", description: "Massimo isolamento termico e acustico", price: "Da €280/mq" },
    { name: "Alluminio", description: "Design moderno e resistenza massima", price: "Da €320/mq" },
    { name: "Legno", description: "Eleganza naturale e tradizione", price: "Da €450/mq" },
    { name: "Alluminio-Legno", description: "Prestazioni superiori e estetica", price: "Da €580/mq" }
  ];

  return (
    <section id="serramenti-dettagli" className="py-20 lg:py-32 bg-gradient-to-br from-amber-50 to-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <DoorOpen className="h-16 w-16 text-amber-600 mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Serramenti <span className="text-amber-600">Infissi-Porte-Blindati</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed text-justify">
            <strong>Sicurezza, efficienza energetica e design per la tua casa.</strong><br />
            Installiamo infissi di alta qualità in PVC, alluminio e legno, porte blindate 
            e sistemi di sicurezza per garantire comfort, risparmio energetico e protezione totale.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Dynamic Image Slider */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl relative">
              {/* Images */}
              {serramentiImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                    index === currentImage
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-105'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay elegante */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-8 left-8 right-8 text-white">
                      <h4 className="text-xl font-bold mb-3">{image.title}</h4>
                      <p className="text-sm text-gray-200 leading-relaxed">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation Controls */}
              <button
                onClick={goToPrevious}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-amber-600/80 hover:bg-amber-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-amber-600/80 hover:bg-amber-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Elegant Dot Indicators */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
                {serramentiImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImage
                        ? 'bg-amber-400 scale-125 shadow-lg'
                        : 'bg-white/60 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>

              {/* Image Counter */}
              <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentImage + 1} / {serramentiImages.length}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Key Info Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="text-center">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-amber-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">3-7</div>
                  <div className="text-sm text-gray-600">Giorni Lavorativi</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Shield className="h-8 w-8 text-green-600 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">10 Anni</div>
                  <div className="text-sm text-gray-600">Garanzia</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-gray-900 mb-1">Certificati</div>
                  <div className="text-sm text-gray-600">CE & ISO</div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Description */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Leader negli Infissi e Serramenti</h3>
              <p className="text-gray-700 leading-relaxed mb-6 text-justify">
                Con <strong>oltre 18.000 installazioni</strong> in tutta Italia, Star Italia è specialista 
                nella fornitura e posa di infissi di alta qualità. Offriamo soluzioni in PVC, alluminio e legno 
                per garantire massimo isolamento termico, sicurezza e design personalizzato per ogni esigenza.
              </p>
              <p className="text-gray-700 leading-relaxed text-justify">
                Dalla <strong>sostituzione completa degli infissi</strong> alle porte blindate, 
                gestiamo tutto il processo: smaltimento vecchi serramenti, pratiche burocratiche, 
                produzione su misura e installazione professionale con certificazioni CE.
              </p>
            </div>

            {/* Typologies */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Tipologie di Infissi</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {windowTypes.map((type, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-gray-900">{type.name}</h4>
                        <span className="text-sm font-semibold text-amber-600">{type.price}</span>
                      </div>
                      <p className="text-sm text-gray-600">{type.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Features List */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cosa Include il Nostro Servizio</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {features.slice(0, 5).map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                {features.slice(5).map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Vantaggi dei Nostri Infissi</h3>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Star className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Sostituisci i Tuoi Infissi Ora!</h3>
              <p className="text-amber-100 mb-6">
                Contattaci per un sopralluogo gratuito e scopri quanto puoi risparmiare 
                sui consumi energetici con i nostri infissi certificati.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-gray-100 font-semibold"
                  onClick={() => window.open('tel:0587346342', '_self')}
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Chiamaci: 0587 346342
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-amber-600"
                  onClick={() => window.open('https://api.whatsapp.com/send?phone=3477030346', '_blank')}
                >
                  <Mail className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SerramentiSection;